<template>
  <div>
    <HMModalHeader
      :title="$t('THANK_YOU.TITLE.SET_UP_YOUR_ACCOUNT')"
      @close="$emit('close')"
    />
    <div class="onboarding-modal-content" v-if="currentPage === 0">
      <h2 class="mt-3">
        {{ $t('THANK_YOU.TITLE.WHAT_DO_YOU_CALL_YOURSELF') }}
      </h2>
      <label>
        {{ $t('THANK_YOU.LABEL.CHOOSE_ONE_OPTION') }}
      </label>
      <ul
        class="list-unstyled d-flex flex-row justify-content-start align-items-center flex-wrap mt-3"
      >
        <li class="mr-3 mb-3" v-for="tag in roles" :key="tag.code">
          <div
            class="tag"
            :class="{ selected: tag.selected }"
            @click="selectRole(tag)"
          >
            {{ tag.label }}
          </div>
        </li>
      </ul>
    </div>
    <div class="onboarding-modal-content" v-if="currentPage === 1">
      <h2 class="mt-3">
        {{ $t('THANK_YOU.TITLE.HOW_WILL_YOU_USE_MARVELOUS') }}
      </h2>
      <label>
        {{ $t('THANK_YOU.LABEL.SELECT_ALL_THAT_APPLY') }}
      </label>
      <ul
        class="list-unstyled d-flex flex-row justify-content-start align-items-center flex-wrap mt-3"
      >
        <li class="mr-3 mb-3" v-for="tag in uses" :key="tag.code">
          <div
            class="tag"
            :class="{ selected: tag.selected }"
            @click="selectUse(tag)"
          >
            {{ tag.label }}
          </div>
        </li>
      </ul>
    </div>
    <div class="onboarding-modal-content" v-if="currentPage === 2">
      <h2 class="mt-3">
        {{ $t('THANK_YOU.TITLE.HOW_DID_YOU_HEAR_ABOUT_MARVELOUS') }}
      </h2>
      <label>
        {{ $t('THANK_YOU.LABEL.LET_US_KNOW_WHAT_BROUGHT_YOU') }}
      </label>
      <ul
        class="list-unstyled d-flex flex-row justify-content-start align-items-center flex-wrap mt-3"
      >
        <li class="mr-3 mb-3" v-for="tag in sources" :key="tag.code">
          <div
            class="tag"
            :class="{ selected: tag.selected }"
            @click="selectSource(tag)"
          >
            {{ tag.label }}
          </div>
        </li>
      </ul>
    </div>
    <div class="onboarding-modal-content" v-if="currentPage === 3">
      <img src="" />
      <h2 class="mt-5">
        {{ $t('THANK_YOU.TITLE.HOLD_TIGHT') }}
      </h2>
      <span class="mt-3">
        {{ $t('THANK_YOU.LABEL.WE_ARE_BUILDING_YOUR_BRAND_NEW_SITE') }}
      </span>
    </div>
    <hr />
    <div
      class="footer d-flex flex-row-reverse align-items-center justify-content-between mb-4"
    >
      <HMButton
        v-if="currentPage < 2"
        @click="currentPage += 1"
        class="align-self-end"
        color="brand"
        :disabled="!isNextEnabled"
      >
        {{ $t('GENERAL.BUTTON.NEXT') }}
      </HMButton>
      <HMButton
        v-if="currentPage === 2"
        @click="finish"
        class="align-self-end"
        color="brand"
        :disabled="!isNextEnabled || finishing"
        :loading="finishing"
      >
        {{ $t('GENERAL.BUTTON.FINISH') }}
      </HMButton>
      <HMButton
        v-if="currentPage > 0"
        @click="currentPage -= 1"
        class="align-self-start"
        color="outline-gray"
      >
        {{ $t('GENERAL.BUTTON.BACK') }}
      </HMButton>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';

export default {
  name: 'StudioOnboardingModal',
  props: {
    onFinish: null,
  },
  computed: {
    ...mapGetters({
      teacher: 'user/teacher',
    }),
    selectedRole() {
      return this.roles.find(role => role.selected);
    },
    selectedUses() {
      return this.uses.filter(use => use.selected);
    },
    selectedSource() {
      return this.sources.find(source => source.selected);
    },
    isNextEnabled() {
      if (this.currentPage === 0) {
        return !!this.selectedRole;
      }
      if (this.currentPage === 1) {
        return this.selectedUses?.length > 0;
      }
      return !!this.selectedSource;
    },
  },
  data() {
    return {
      currentPage: 0,
      finishing: false,
      roles: [
        {
          code: 'yoga teacher',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_YOGA_TEACHER'),
        },
        {
          code: 'fitness instructor',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_FITNESS_INSTRUCTOR'),
        },
        {
          code: 'pilates instructor',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_PILATES_INSTRUCTOR'),
        },
        {
          code: 'physical studio owner',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_PHYSICAL_STUDIO_OWNER'),
        },
        {
          code: 'influencer',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_INFLUENCER'),
        },
        {
          code: 'mindset coach',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_MINDSET_COACH'),
        },
        {
          code: 'wellness coach',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_WELLNESS_COACH'),
        },
        {
          code: 'business coach',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_BUSINESS_COACH'),
        },
        {
          code: 'life coach',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_LIFE_COACH'),
        },
        {
          code: 'designer',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_DESIGNER'),
        },
        {
          code: 'copywriter',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_COPYWRITER'),
        },
        {
          code: 'developer',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_DEVELOPER'),
        },
        {
          code: 'photographer',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_PHOTOGRAPHER'),
        },
        {
          code: 'course creator',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_COURSE_CREATOR'),
        },
        {
          code: 'illustrator',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_ILLUSTRATOR'),
        },
        {
          code: 'service provider',
          label: this.$t('THANK_YOU.OPTIONS.ROLE_SERVICE_PROVIDER'),
        },
      ],
      uses: [
        {
          code: 'online memberships',
          label: this.$t('THANK_YOU.OPTIONS.USE_ONLINE_MEMBERSHIPS'),
        },
        {
          code: 'courses',
          label: this.$t('THANK_YOU.OPTIONS.USE_COURSES'),
        },
        {
          code: 'digital downloads',
          label: this.$t('THANK_YOU.OPTIONS.USE_DIGITAL_DOWNLOADS'),
        },
        {
          code: 'free webinars',
          label: this.$t('THANK_YOU.OPTIONS.USE_FREE_WEBINARS'),
        },
        {
          code: 'private coaching',
          label: this.$t('THANK_YOU.OPTIONS.USE_PRIVATE_COACHING'),
        },
        {
          code: 'virtual events',
          label: this.$t('THANK_YOU.OPTIONS.USE_VIRTUAL_EVENTS'),
        },
        {
          code: 'in-person classes',
          label: this.$t('THANK_YOU.OPTIONS.USE_IN_PERSON_CLASSES'),
        },
        {
          code: 'grouops or communities',
          label: this.$t('THANK_YOU.OPTIONS.USE_GROUPS_OR_COMMUNITIES'),
        },
        {
          code: 'drop-in classes',
          label: this.$t('THANK_YOU.OPTIONS.USE_DROP_IN_CLASSES'),
        },
        {
          code: 'product bundles',
          label: this.$t('THANK_YOU.OPTIONS.USE_PRODUCT_BUNDLES'),
        },
      ],
      sources: [
        {
          code: 'affiliate link',
          label: this.$t('THANK_YOU.OPTIONS.SOURCE_AFFILIATE_LINK'),
        },
        {
          code: 'online search',
          label: this.$t('THANK_YOU.OPTIONS.SOURCE_ONLINE_SEARCH'),
        },
        {
          code: 'word of mouth',
          label: this.$t('THANK_YOU.OPTIONS.SOURCE_WORD_OF_MOUTH'),
        },
        {
          code: 'instagram',
          label: this.$t('THANK_YOU.OPTIONS.SOURCE_INSTAGRAM'),
        },
        {
          code: 'facebook',
          label: this.$t('THANK_YOU.OPTIONS.SOURCE_FACEBOOK'),
        },
        {
          code: 'youtube',
          label: this.$t('THANK_YOU.OPTIONS.SOURCE_YOUTUBE'),
        },
        {
          code: 'linkedin',
          label: this.$t('THANK_YOU.OPTIONS.SOURCE_LINKEDIN'),
        },
        {
          code: 'twitter',
          label: this.$t('THANK_YOU.OPTIONS.SOURCE_TWITTER_X'),
        },
        {
          code: 'podcast',
          label: this.$t('THANK_YOU.OPTIONS.SOURCE_PODCAST'),
        },
        {
          code: 'tiktok',
          label: this.$t('THANK_YOU.OPTIONS.SOURCE_TIKTOK'),
        },
      ],
    };
  },
  methods: {
    async finish() {
      this.finishing = true;
      try {
        await this.onFinish(
          this.selectedRole.code,
          this.selectedUses.map(u => u.code),
          this.selectedSource.code
        );
      } finally {
        this.finishing = false;
        this.$emit('close');
      }
    },
    selectRole(role) {
      this.roles.forEach(r => Vue.set(r, 'selected', false));
      Vue.set(role, 'selected', !role.selected);
    },
    selectSource(source) {
      this.sources.forEach(s => Vue.set(s, 'selected', false));
      Vue.set(source, 'selected', !source.selected);
    },
    selectUse(use) {
      Vue.set(use, 'selected', !use.selected);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.onboarding-modal-content {
  max-width: 800px;
}

.tag {
  border-radius: $border-radius;
  padding: 0.5rem 0.75rem;
  border: 1px solid $medium-gray;
  color: $dark-gray;
  background-color: transparent;
  cursor: pointer;

  &.selected {
    background-color: $medium-gray;
    color: $charcoal;
  }
}
</style>
